import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { useUser } from '@/contexts/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Shield, Eye, Users, Globe, Lock } from 'lucide-react';

interface PrivacyPreferences {
  profile_visibility: 'public' | 'authenticated' | 'private';
  show_email: boolean;
  show_location: boolean;
  show_achievements: boolean;
  show_deals: boolean;
  data_sharing: boolean;
  analytics_tracking: boolean;
}

const DEFAULT_PREFS: PrivacyPreferences = {
  profile_visibility: 'authenticated',
  show_email: false,
  show_location: true,
  show_achievements: true,
  show_deals: false,
  data_sharing: false,
  analytics_tracking: true,
};

export const PrivacySettings = () => {
  const { currentUser } = useUser();
  const { toast } = useToast();
  const [settings, setSettings] = useState<PrivacyPreferences>(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadPrivacySettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  const loadPrivacySettings = async () => {
    if (!currentUser?.id) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('privacy_preferences')
        .eq('id', currentUser.id)
        .maybeSingle();

      if (error) throw error;

      const prefs = data?.privacy_preferences as Partial<PrivacyPreferences> | null | undefined;
      if (prefs && typeof prefs === 'object' && !Array.isArray(prefs)) {
        setSettings({ ...DEFAULT_PREFS, ...prefs });
      }
    } catch (error) {
      console.error('Error loading privacy settings:', error);
    }
  };

  const updateSetting = <K extends keyof PrivacyPreferences>(key: K, value: PrivacyPreferences[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const saveSettings = async () => {
    if (!currentUser?.id) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ privacy_preferences: settings })
        .eq('id', currentUser.id);

      if (error) throw error;

      toast({
        title: 'Saved',
        description: 'Your privacy preferences have been updated.',
      });
    } catch (error) {
      console.error('Error saving privacy settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to save privacy settings. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public': return <Globe className="h-4 w-4" />;
      case 'authenticated': return <Users className="h-4 w-4" />;
      case 'private': return <Lock className="h-4 w-4" />;
      default: return <Eye className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Profile Visibility
          </CardTitle>
          <CardDescription>
            Control who can see your profile and information.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Who can see your profile</Label>
            <Select 
              value={settings.profile_visibility} 
              onValueChange={(value) => updateSetting('profile_visibility', value)}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Public - Anyone can see your profile
                  </div>
                </SelectItem>
                <SelectItem value="authenticated">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Authenticated Users - Only logged-in users
                  </div>
                </SelectItem>
                <SelectItem value="private">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    Private - Only you can see your profile
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-4 pt-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="show-email">Show email address</Label>
              <Switch
                id="show-email"
                checked={settings.show_email}
                onCheckedChange={(checked) => updateSetting('show_email', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="show-location">Show location</Label>
              <Switch
                id="show-location"
                checked={settings.show_location}
                onCheckedChange={(checked) => updateSetting('show_location', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="show-achievements">Show achievements and stats</Label>
              <Switch
                id="show-achievements"
                checked={settings.show_achievements}
                onCheckedChange={(checked) => updateSetting('show_achievements', checked)}
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label htmlFor="show-deals">Show deals and partnerships</Label>
              <Switch
                id="show-deals"
                checked={settings.show_deals}
                onCheckedChange={(checked) => updateSetting('show_deals', checked)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Data & Analytics
          </CardTitle>
          <CardDescription>
            Manage how your data is used and shared.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="data-sharing">Allow data sharing with partners</Label>
              <p className="text-sm text-muted-foreground">
                Share anonymized data to improve platform features
              </p>
            </div>
            <Switch
              id="data-sharing"
              checked={settings.data_sharing}
              onCheckedChange={(checked) => updateSetting('data_sharing', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="analytics-tracking">Analytics tracking</Label>
              <p className="text-sm text-muted-foreground">
                Help us improve the platform with usage analytics
              </p>
            </div>
            <Switch
              id="analytics-tracking"
              checked={settings.analytics_tracking}
              onCheckedChange={(checked) => updateSetting('analytics_tracking', checked)}
            />
          </div>
        </CardContent>
      </Card>

      <Button onClick={saveSettings} disabled={isLoading} className="w-full">
        {isLoading ? 'Saving...' : 'Save Privacy Settings'}
      </Button>
    </div>
  );
};